import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation, useQuery, useLazyQuery, useApolloClient } from '@apollo/client/react';
import {
  CHARGE_SUBSCRIPTION_USER,
  CHARGE_SUBSCRIPTION_SPECIAL,
  GET_UPCOMING_SUBSCRIPTION_REVIEW,
  STRIPE_BUILD_ELEMENT,
  PLAN_FEATURES_QUERY,
  GET_TOTAL_SCREENS_COUNT,
  UPDATE_PROMO_CODE_SUBSCRIPTION,
  GET_COUPON_STRIPE,
  VALIDATE_CHANGE_PLAN,
  GET_UPCOMING_SUBSCRIPTION,
  GET_DEVICE_FREES,
  UPDATE_TEAM,
  SUBMIT_USER_SURVEY,
  GET_COMPANY_WITH_MEMBERS,
} from '@scio3/graphql-client';
import type {
  ChargeSubscriptionVariables,
  UpcomingSubscriptionReviewVariables,
  UpcomingSubscriptionReviewData,
  StripeBuildElementVariables,
  TotalScreensCountResponse,
  SubmitUserSurveyVariables,
} from '@scio3/graphql-client';
import {
  PLAN_FEATURES,
  BILLING_INTERVAL,
  PAYMENT_STATUS_CODE,
  ROOT_TEAM_ID,
  FREE_PLAN_LIMITS,
} from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import {
  analyticsSubscriptionCreated,
  analyticsSubscriptionUpdated,
} from '@/lib/analytics-service';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useBillingData } from './useBillingData';
import { syncMeForReferralPrompt } from './syncMeForReferralPrompt';
import {
  getPlanDisplayName,
  formatCurrencyAmount,
  getCurrencySymbol,
  extractPlanFeaturesNode,
  extractMutationResponse,
} from '../utils/plan-helpers';
import { BILLING_INTERVAL_LABELS, ADDON_TIER_CATALOG } from '../utils/billing-constants';
import { isEngageAddonRedundantOnPlan } from '../utils/engage-addon';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Wizard step constants matching scio1's steps.constSteps */
export const STEP = {
  NUMBER_SCREEN: 'NUMBER_SCREEN',
  PLAN: 'PLAN',
  CONFIRMATION: 'CONFIRMATION',
  FINISH: 'FINISH',
} as const;

export type WizardStep = (typeof STEP)[keyof typeof STEP];

export const WIZARD_STEPS = [
  STEP.NUMBER_SCREEN,
  STEP.PLAN,
  STEP.CONFIRMATION,
  STEP.FINISH,
] as const;

/** Trial users: PLAN first so they choose what they want before sizing it.
 *  Paid users: NUMBER_SCREEN first because plan is already locked in. */
export const TRIAL_WIZARD_STEPS = [
  STEP.PLAN,
  STEP.NUMBER_SCREEN,
  STEP.CONFIRMATION,
  STEP.FINISH,
] as const;

interface PlanPriceEntry {
  stripe_plan_id: string;
  price: number;
  name?: string;
  discount?: number;
  text?: string;
  discount_text?: string;
}

interface PlanAlias {
  _id?: string;
  plans?: Record<string, PlanPriceEntry>;
  name?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getPlanPrice(
  plans: Record<string, Record<string, PlanPriceEntry>> | undefined,
  currency: string,
  interval: BillingInterval,
): number {
  return plans?.[currency]?.[interval]?.price ?? 0;
}

const INTERVAL_TO_GQL = {
  monthly: 'MONTHLY',
  yearly: 'YEARLY',
  yearly_freedevice: 'YEARLY_FREEDEVICE',
} as const satisfies Record<BillingInterval, string>;

function getPlanIdForGraphQL(
  interval: BillingInterval,
): 'MONTHLY' | 'YEARLY' | 'YEARLY_FREEDEVICE' {
  return INTERVAL_TO_GQL[interval];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSubscriptionWizard() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const openScreenManagement = useBillingDialogStore((s) => s.openScreenManagement);
  const openDowngradeSurvey = useBillingDialogStore((s) => s.openDowngradeSurvey);
  const openDowngradeComparison = useBillingDialogStore((s) => s.openDowngradeComparison);
  const openMemberDowngradeWarning = useBillingDialogStore((s) => s.openMemberDowngradeWarning);
  // Identifies which cancel-sub CTA opened the wizard (if any). On successful
  // plan-update we write a userSurveys row attributed to that source, then
  // clear it. Plan changes started from the normal Subscription page never
  // set this — they don't write a row.
  const cancelFlowSource = useBillingDialogStore((s) => s.cancelFlowSource);
  const setCancelFlowSource = useBillingDialogStore((s) => s.setCancelFlowSource);
  // Snapshot the paywall state so the close handler knows whether to hard-reload.
  // We hard-reload only when wizard was opened from the paywall lockout AND a payment
  // succeeded (chargeResult is set) — this lifts isAccountInactive and clears all caches.
  const isAccountInactive = useAuthStore((s) => s.isAccountInactive);
  // Bug 6 — referral expansion prompt. AuthProvider populates the auth-store
  // `me` mirror via one-off client.query() (no live observable), so the charge
  // mutation's refetchQueries:['Me'] refreshes the Apollo cache but never
  // re-runs setMe → the widget (which reads me.referralPromptPending from the
  // store) stays stale until reload/window-focus. After a prompt-eligible
  // finish we manually re-sync me into the store and bounded-poll for the
  // (webhook-driven) prompt. Mirrors PaymentMethodPage's post-card-save
  // re-sync. See syncMeForReferralPrompt.ts.
  const apolloClient = useApolloClient();
  const setMe = useAuthStore((s) => s.setMe);
  const isOpen = activeDialog === 'subscriptionWizard';
  const contextSubAccountId = dialogContext.subAccountId as string | undefined;
  const contextTeamId = dialogContext.teamId as string | undefined;
  // Caller-provided title override — proactive entry points (e.g. trial banner) pass
  // friendlier wording like "Select Your Plan" instead of the gate-style "Upgrade Required".
  // Sourced from `dialogContext.wizardTitle` (NOT `dialogContext.title`) to avoid colliding
  // with legacy `title: t('Change Plan')` args that other callers pass for the scio1 iframe path.
  const contextTitle = dialogContext.wizardTitle as string | undefined;
  // Opt-in caller signal: which plan card to highlight on the plan-comparison step.
  // Set ONLY by callers that explicitly want a recommendation (e.g. PromotionalBanner).
  // Distinct from `planFeature`/`preSelectedPlan` which other callers overload to echo
  // the user's current plan or set the floor — those would cause cross-caller side
  // effects if reused for highlight state, so we use a dedicated field.
  const contextRecommendedPlan = dialogContext.recommendedPlan as string | undefined;
  // Opt-in caller signal: which surface initiated the purchase (e.g. the rooms
  // license wall passes 'room-activate'). Forwarded to the success analytics as
  // the `purchase_source` PostHog property; absent = unattributed.
  const contextPurchaseSource = dialogContext.purchaseSource as string | undefined;
  const contextMinPlan = (dialogContext.minPlanRequired ?? dialogContext.preSelectedPlan) as
    | string
    | undefined;
  const skipPlanStep = !!dialogContext.skipPlanStep;
  const fromInactive = !!dialogContext.fromInactive;
  const redirectChargeResult = dialogContext.redirectChargeResult as
    | Record<string, unknown>
    | undefined;

  // useBillingData with subAccountId → automatically fetches fresh sub-account data
  const {
    payment,
    maxScreens,
    usedScreens,
    planFeature: currentPlanFeature,
    isTrial,
    addons: paymentAddons,
    currency: baseCurrency,
    existingCard,
    defaultEmail,
    loadingSubAccount,
    teamName: currentTeamName,
    teamDescription: currentTeamDescription,
    company,
    canChangeSub,
  } = useBillingData({ subAccountId: contextSubAccountId, teamId: contextTeamId, skip: !isOpen });

  // Enterprise / custom-pricing accounts: pricing lives in Stripe, only the
  // screen quantity is editable from the wizard. Mirrors SCIO 1.0 derivation
  // in subscription.js:473 + subscriptionPlanModal.js:1085 — when set we
  // short-circuit the submit path to chargeSubscriptionSpecial below.
  const isSyncPriceStripe =
    !canChangeSub || !!(payment as Record<string, unknown> | undefined)?.isSyncPriceStripe;

  const subAccountDataReady = (!contextSubAccountId && !contextTeamId) || !loadingSubAccount;

  // #1232 — Cross-team aggregate of paired screens. Query placed here (ahead of
  // wizard state) so `effectiveUsedScreens` below is in scope for the init
  // effect that seeds `screenCount`. `usedScreens` from useBillingData is
  // scope-specific by design (#49); a Trial account at root scope does NOT
  // count screens assigned to member teams. Backend `chargeSubscription`
  // validates against the full subscription perimeter, so initialising
  // `screenCount` from `usedScreens` alone caused LIMIT_QUANTITY rejections
  // for the trial-cross-team case (#1232). `getTotalScreensCount.totalTeamUsed`
  // is the same aggregate the backend uses; `minScreens` (defined below) also
  // consumes it.
  const { data: screensCountData } = useQuery<TotalScreensCountResponse>(GET_TOTAL_SCREENS_COUNT, {
    variables: { subAccountId: contextSubAccountId, teamId: contextTeamId },
    skip: !isOpen,
    fetchPolicy: 'network-only',
  });
  const effectiveUsedScreens = screensCountData?.getTotalScreensCount?.totalTeamUsed ?? usedScreens;

  // ---- Wizard state ----
  // Skip to plan step when opened from upgrade context (preSelectedPlan / minPlanRequired).
  // Trial users normally start on the PLAN step (plan-first flow); paid users start on
  // NUMBER_SCREEN. When `skipPlanStep` is set (sub-account / team billing), PLAN is hidden
  // entirely — trial flows that match those callers must start at NUMBER_SCREEN too.
  const [step, setStep] = useState<WizardStep>(
    contextMinPlan || (isTrial && !skipPlanStep) ? STEP.PLAN : STEP.NUMBER_SCREEN,
  );
  // Step sequence — trial users follow PLAN → NUMBER_SCREEN → CONFIRMATION; paid users use the
  // legacy NUMBER_SCREEN → PLAN → CONFIRMATION. Drives stepIndex / goNext / goBack.
  // When `skipPlanStep` is set, drop PLAN from the sequence so goNext/goBack never lands on it.
  const wizardSteps: readonly WizardStep[] = skipPlanStep
    ? (isTrial ? TRIAL_WIZARD_STEPS : WIZARD_STEPS).filter((s) => s !== STEP.PLAN)
    : isTrial
      ? TRIAL_WIZARD_STEPS
      : WIZARD_STEPS;
  // #1136 — Wizard's screenCount must default to at least the user's currently
  // paired (non-OnHold) device count. Otherwise the user could submit a purchase
  // for fewer screens than they have active and either bypass the BE guard
  // (older clients) or get rejected with a confusing error.
  //
  // Trial: floor of 1 (minimum purchase), bumped up if usedScreens is higher.
  //   - scio1 default was 1 always — that's the bug; trial accounts can have
  //     paired devices and selecting 1 below paired count is wrong.
  // Paid:  floor of maxScreens (current license count), bumped up if usedScreens
  //   is higher (Free plan with leftover paired devices is the canonical case).
  const [screenCount, setScreenCount] = useState(
    Math.max(isTrial ? 1 : maxScreens, usedScreens) || 1,
  );
  // SP45 — single source of truth for "user's effective plan is Free". Drives the
  // Free-row visibility (via hideFreePlanOption exposed below).
  const isOnFreePlan = currentPlanFeature === PLAN_FEATURES.FREE_PLAN;
  // No longer normalize pro → proPlus. The current plan stays `pro` so it drives the
  // default selection below (`defaultSelectedPlan`): a quantity-only change keeps `pro`,
  // and the user upgrades by picking the Pro Plus card. The retired `pro` card itself is
  // NOT rendered (isActive=false → hidden in PlanComparisonTable); we just need `pro` as
  // the selected/charged default. This fixes "legacy Pro cannot upgrade to Pro Plus".
  const normalizedCurrentPlan = currentPlanFeature;
  // scio1 parity (subscriptionPlanModal.js getPlanFeatures, lines 588-616):
  // - Free / no current plan → default to Standard (first non-Free, the upgrade path).
  // - Has a current paid plan → default to that current plan.
  const defaultSelectedPlan =
    !isOnFreePlan && normalizedCurrentPlan ? normalizedCurrentPlan : PLAN_FEATURES.STANDARD;
  // scio1 parity: when minPlanRequired is set, scio1 mutates payment.planFeature to
  // Pro Plus (the upsell tier) unless the min IS Engage, in which case it sticks with
  // Engage. Replicate that here so a "needs at least Standard" gate pre-selects Pro Plus.
  const minPlanPreSelect = contextMinPlan
    ? contextMinPlan === PLAN_FEATURES.ENGAGE
      ? PLAN_FEATURES.ENGAGE
      : PLAN_FEATURES.PRO_PLUS
    : undefined;
  const [selectedPlan, setSelectedPlan] = useState(
    contextRecommendedPlan ?? minPlanPreSelect ?? defaultSelectedPlan,
  );
  // The charged plan is exactly what the user selected. A legacy pro account now defaults
  // to the real `pro` card (via normalizedCurrentPlan above), so a pure quantity change
  // (e.g. adding a screen) keeps `pro` ($12.50/screen) with no special-case force, while
  // clicking the Pro Plus card is a genuine upgrade ($15.00/screen). The previous guard
  // that forced pro→pro is gone — it also blocked the legitimate Pro→Pro Plus upgrade.
  const submitPlanAlias = selectedPlan;
  // Team-billing only: Team Name + Purpose editable below the screen counter
  // (scio1 parity — Issue #49). Synced from useBillingData when team data loads.
  const [teamNameInput, setTeamNameInput] = useState('');
  const [teamDescriptionInput, setTeamDescriptionInput] = useState('');

  // #1275 — Free/Standard plans don't support Teams. Block downgrading to a no-Team
  // plan while switched into a non-default team (would trap the Owner inside the team).
  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  const teams = useTeamStore((s) => s.teams);
  // #1460 — `company.feature.allowTeams` is a PLAN CAPABILITY flag (true for any
  // Pro Plus tier, including trials), NOT a signal that teams actually exist. The
  // "move screens & delete teams" downgrade warning must only fire when the
  // account has at least one non-Default team. Otherwise a Pro Plus trial with
  // zero created teams wrongly sees the warning when downgrading to Free.
  const hasCreatedTeams = teams.some((t) => t._id !== ROOT_TEAM_ID);
  const [teamPlanBlockOpen, setTeamPlanBlockOpen] = useState(false);
  const dismissTeamPlanBlock = useCallback(() => setTeamPlanBlockOpen(false), []);
  const switchToDefaultTeam = useCallback(() => {
    setActiveTeam(ROOT_TEAM_ID);
    // Mirror TeamSwitcher: reload so the new (Default Team) context is applied everywhere.
    window.location.href = window.location.pathname;
  }, [setActiveTeam]);

  // Sync screenCount/plan from fresh billing data when dialog opens
  const initDoneRef = useRef(false);
  // Tracks whether the Team Name / Purpose inputs have been seeded from the
  // freshly-loaded team doc this dialog session. Separate from `initDoneRef`
  // because team data resolves AFTER company data — see the dedicated effect
  // below.
  const teamInputsInitedRef = useRef(false);
  // Guard: blocks doGoNext while plan change validation is in-flight
  const validatingPlanRef = useRef(false);
  // State version of the flag — triggers re-render to show loading spinner on Next button
  const [validatingPlan, setValidatingPlan] = useState(false);
  // Flag: skip validation on next doGoNext call (set by confirmChangePlanWarning after user confirms)
  const skipNextValidationRef = useRef(false);
  // Single-flight guard for Select Free — prevents duplicate mutation calls / duplicate DowngradeDialog opens
  // when the user clicks the button repeatedly before the async handler completes.
  const selectingFreeRef = useRef(false);
  // State version of the flag — drives the disabled prop on the Free button so it also reflects visually.
  const [isSelectingFree, setIsSelectingFree] = useState(false);
  // Tracks whether the user entered the PLAN step via the cancel flow's "I want
  // to switch plans" reason. When true, picking Free skips the DowngradeSurvey
  // ("Why are you downgrading?") and opens DowngradeComparisonDialog directly,
  // because the user already answered an equivalent survey on the cancel side.
  const fromCancelFlowRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      initDoneRef.current = false;
      // Drop the cancel-flow bypass too so the next time the wizard opens
      // outside a cancel context, Free-plan selection shows the survey again.
      fromCancelFlowRef.current = false;
      // Reset to the first step of the user's flow so a re-open doesn't reuse the last step
      // reached in the previous session (e.g. CONFIRMATION / FINISH). Trial users start at PLAN
      // unless `skipPlanStep` is set (sub-account / team billing) — then start at NUMBER_SCREEN.
      setStep(isTrial && !skipPlanStep ? STEP.PLAN : STEP.NUMBER_SCREEN);
      return;
    }
    // Redirect flow (iDEAL/Bancontact): jump directly to FINISH with charge result
    if (redirectChargeResult) {
      setChargeResult(redirectChargeResult);
      setStep(STEP.FINISH);
      initDoneRef.current = true;
      return;
    }
    if (initDoneRef.current || maxScreens <= 0) return;
    initDoneRef.current = true;
    // #1136 — same Math.max guard as the useState init above; covers the case
    // where the dialog opens before maxScreens has loaded. Applies to both
    // trial AND paid: floor is 1 / maxScreens respectively, bumped to
    // effectiveUsedScreens whenever paired count is higher.
    // #1232 — use cross-team aggregate so a Trial-root user with screens in member
    // teams initialises to the true paired count (not the root-scope-only count).
    setScreenCount(Math.max(isTrial ? 1 : maxScreens, effectiveUsedScreens) || 1);
    setSelectedPlan(contextRecommendedPlan ?? minPlanPreSelect ?? defaultSelectedPlan);
    if (contextMinPlan || (isTrial && !skipPlanStep)) {
      setStep(STEP.PLAN);
    } else if (skipPlanStep) {
      // Force NUMBER_SCREEN when PLAN is suppressed — useState init may have
      // been computed before isTrial/skipPlanStep were ready.
      setStep(STEP.NUMBER_SCREEN);
    }
  }, [
    isOpen,
    maxScreens,
    effectiveUsedScreens,
    defaultSelectedPlan,
    contextRecommendedPlan,
    contextMinPlan,
    minPlanPreSelect,
    isTrial,
    skipPlanStep,
    redirectChargeResult,
  ]);

  // Team Name + Purpose seeding (Issue #49). Split out from the main init
  // effect because `useBillingData` resolves company data (maxScreens, plan)
  // BEFORE the team-specific `getTeamBillingInfo` query returns. Folding the
  // team-input seed into the gated `initDoneRef` block would latch with
  // `currentTeamName = undefined`, leaving the inputs empty and causing
  // `handleCharge` to send `updateTeam` with `name: ''` (backend then rejects
  // with "Team name is required"). This effect fires once per dialog open
  // when the team doc has actually loaded, and won't overwrite later user
  // edits because `teamInputsInitedRef` latches after the first seed.
  useEffect(() => {
    if (!isOpen) {
      teamInputsInitedRef.current = false;
      return;
    }
    if (!contextTeamId) return;
    if (teamInputsInitedRef.current) return;
    if (currentTeamName === undefined) return; // team doc not loaded yet
    teamInputsInitedRef.current = true;
    setTeamNameInput(currentTeamName);
    setTeamDescriptionInput(currentTeamDescription ?? '');
  }, [isOpen, contextTeamId, currentTeamName, currentTeamDescription]);

  // #1232 — Late-arriving cross-team count. The init effect above is one-shot
  // (gated by `initDoneRef`), so if `screensCountData` resolves AFTER `maxScreens`,
  // `effectiveUsedScreens` won't have been picked up. Bump `screenCount` upward
  // (never downward) once we learn the true paired count. The screen selector's
  // `min={minScreens}` already prevents the user from reducing below this floor.
  useEffect(() => {
    if (!isOpen || !initDoneRef.current) return;
    setScreenCount((prev) => Math.max(prev, effectiveUsedScreens));
  }, [isOpen, effectiveUsedScreens]);

  const currentPlan = (payment?.plan as string) ?? BILLING_INTERVAL.YEARLY;
  const initialInterval = (
    currentPlan === BILLING_INTERVAL.MONTHLY
      ? BILLING_INTERVAL.MONTHLY
      : currentPlan === BILLING_INTERVAL.YEARLY_FREEDEVICE
        ? BILLING_INTERVAL.YEARLY_FREEDEVICE
        : BILLING_INTERVAL.YEARLY
  ) as BillingInterval;
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(initialInterval);
  useEffect(() => {
    if (isOpen && payment?.plan) {
      setBillingInterval(initialInterval);
    }
  }, [isOpen, payment?.plan, initialInterval]);
  // Currency override — trial users can change currency in the plan step
  const [currencyOverride, setCurrencyOverride] = useState<string | null>(null);
  const effectiveCurrency = currencyOverride ?? baseCurrency;
  const effectiveCurrencySymbol = getCurrencySymbol(effectiveCurrency);

  // No cached prorationDate: useState(() => Now) captures the value at mount,
  // which becomes stale if the subscription changes mid-session (e.g. a trial→
  // paid conversion at Step 8 advances the subscription's current_period_start
  // past the cached Now → Stripe rejects subscription_proration_date as
  // "outside current subscription period" → outer catch returns total: 0 →
  // Confirmation shows $0.00. Computed fresh per call now (#1175 follow-up).
  const [useExistingCardOverride, setUseExistingCard] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Tracks Stripe form processing (between form submit and confirmSetup completing).
  // For redirect-based methods (iDEAL), this stays true until the browser redirects.
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [discountLabel, setDiscountLabel] = useState<string | null>(null);
  const [chargeResult, setChargeResult] = useState<Record<string, unknown> | null>(null);
  const [showScreenManagement, setShowScreenManagement] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [skippedToConfirmation, setSkippedToConfirmation] = useState(false);
  const [showChangePlanWarning, setShowChangePlanWarning] = useState(false);
  const [changePlanWarnings, setChangePlanWarnings] = useState<
    Array<{
      messageDisplay?: string;
      textValues?: string[];
      requiredRemove?: boolean;
      url?: string;
      plan?: string;
    }>
  >([]);
  const [changePlanWarningConfirmLabel, setChangePlanWarningConfirmLabel] = useState<
    string | undefined
  >(undefined);

  // ---- Queries ----
  // (`screensCountData` query moved up above the wizard state — see #1232 comment.)
  const { data: upcomingSubData } = useQuery(GET_UPCOMING_SUBSCRIPTION, {
    variables: {
      isTrial: false,
      stripe_plan_id: '',
      subAccountId: contextSubAccountId,
      teamId: contextTeamId,
    },
    skip: !isOpen || isTrial,
    fetchPolicy: 'network-only',
  });

  // Existing free-device bundles — SCIO 1.0 parity (subscriptionPlanModal.js
  // deviceFreesOrigin reactive). Needed so the change flow UPDATEs the current
  // bundle instead of inserting a duplicate when the user increases FD screens.
  // Skip on trial because there are no existing bundles yet.
  const { data: deviceFreesData } = useQuery(GET_DEVICE_FREES, {
    variables: { subAccountId: contextSubAccountId, teamId: contextTeamId },
    skip: !isOpen || isTrial,
    fetchPolicy: 'network-only',
  });
  const existingDeviceFrees = useMemo(() => {
    const raw = (deviceFreesData as Record<string, unknown>)?.getDeviceFrees as
      | Array<{ _id: string; maxQuantity: number; expiredDate?: string }>
      | undefined;
    return Array.isArray(raw) ? raw : [];
  }, [deviceFreesData]);

  // --- Per-bundle editable state (SCIO 1.0 subscriptionPlanModal.js `deviceFrees`
  //     reactive with `maxQuantityOption` cap). Only used for yearly_freedevice
  //     on the Confirmation step; other intervals keep the single-counter flow. ---
  type EditableBundle = {
    _id?: string;
    maxQuantity: number;
    /** SCIO 1.0: existing bundles can only decrease (max = original qty); new bundles = 9999. */
    maxQuantityOption: number;
    expiredDate?: string;
  };
  const [editedDeviceFrees, setEditedDeviceFrees] = useState<EditableBundle[]>([]);
  const editedBundlesInitRef = useRef<string>('');

  const initialCredit = useMemo(() => {
    const raw = (upcomingSubData as Record<string, unknown>)?.getUpcomingSubscription as
      | Record<string, unknown>
      | undefined;
    return (raw?.credit as number) ?? 0;
  }, [upcomingSubData]);

  const minScreens = useMemo(() => {
    // QC #1310 — scio1 parity. Floor depends on context:
    //
    //  - Team-billing: floor = paired count (0 when no screens paired).
    //    scio1 allows reducing screens of a billing team to 0 and calling
    //    Update — that's how team billing is cancelled (no explicit Cancel
    //    Subscription button). Backend treats numberOfScreens=0 as
    //    deactivation. We use team-scoped `usedScreens` (team.limitation.
    //    quantity from useBillingData) directly here — NOT the cross-team
    //    aggregate `totalTeamUsed`, which can be inflated by reserved/quota
    //    rather than paired count.
    //
    //  - Sub-account / parent: floor = max(1, paired count). scio1 shows
    //    an explicit Cancel Subscription button (TypeForm survey) and the
    //    stepper floors at 1; cancel goes through the survey, not the
    //    stepper.
    if (contextTeamId) {
      return Math.max(0, usedScreens);
    }
    const counts = screensCountData?.getTotalScreensCount;
    if (!counts) return Math.max(1, maxScreens || 1);
    const inUse = counts.totalTeamUsed ?? usedScreens + (counts.totalReserved ?? 0);
    return Math.max(1, inUse);
  }, [screensCountData, usedScreens, maxScreens, contextTeamId]);

  const { data: planFeaturesData } = useQuery(PLAN_FEATURES_QUERY, {
    // Resolve the CHARGED plan from submitPlanAlias (keeps legacy `pro`), not the
    // display-normalized selectedPlan — drives planFeatureId, pricing and plan name. See
    // submitPlanAlias above.
    variables: { query: { alias: submitPlanAlias } },
    skip: !isOpen || !submitPlanAlias,
    fetchPolicy: 'network-only',
  });

  const aliasData = useMemo(
    () => extractPlanFeaturesNode<PlanAlias>(planFeaturesData) ?? ({} as PlanAlias),
    [planFeaturesData],
  );

  const aliasPlanData = aliasData?.plans as
    | Record<string, Record<string, PlanPriceEntry>>
    | undefined;
  const monthlyPrice = getPlanPrice(aliasPlanData, effectiveCurrency, BILLING_INTERVAL.MONTHLY);
  const yearlyPrice = getPlanPrice(aliasPlanData, effectiveCurrency, BILLING_INTERVAL.YEARLY);
  const yearlyFreeDevicePrice = getPlanPrice(
    aliasPlanData,
    effectiveCurrency,
    BILLING_INTERVAL.YEARLY_FREEDEVICE,
  );
  const currentPricePerScreen =
    billingInterval === BILLING_INTERVAL.YEARLY_FREEDEVICE
      ? yearlyFreeDevicePrice
      : billingInterval === BILLING_INTERVAL.YEARLY
        ? yearlyPrice
        : monthlyPrice;

  // SCIO 1.0 subscriptionPlanModal.js isShowPlan / isDisablePlan (lines 2303-2330).
  // Gates the "YEARLY + FREE DEVICE" card and disables the Monthly / Yearly cards
  // when the user already has a free-device Stripe subscription.
  const companyFeature = company?.feature;
  const enableYearlyFreeDevice = !!companyFeature?.enableYearlyFreeDevice;
  const allowAddYearlyFreeDevice = !!companyFeature?.allowAddYearlyFreeDevice;
  const allowAddYearly10Plan = !!companyFeature?.allowAddYearly10Plan;
  const allowAddMonthlyPlan = !!companyFeature?.allowAddMonthlyPlan;
  const hasFreeDeviceSub = !!payment?.subscription_freedevice_id;

  const showYearlyFreeDevice = effectiveCurrency === 'usd' && enableYearlyFreeDevice;
  const yearlyFreeDeviceDisabled = (() => {
    if (isTrial) return false;
    if (allowAddYearlyFreeDevice) return false;
    if (hasFreeDeviceSub) return false; // user already has it — allowed to keep selected
    // Not trial, no override, no existing FD sub → disabled (SCIO 1.0 line 2324)
    return true;
  })();
  const monthlyDisabled = (() => {
    if (isTrial) return false;
    if (allowAddMonthlyPlan) return false;
    // Having a free-device sub blocks switching to Monthly (SCIO 1.0 line 2321)
    if (hasFreeDeviceSub) return true;
    return false;
  })();
  const yearlyDisabled = (() => {
    if (isTrial) return false;
    if (allowAddYearly10Plan) return false;
    if (hasFreeDeviceSub) return true;
    return false;
  })();

  // SCIO 1.0 parity (subscriptionPlanModal.js:1262-1280 + :1162-1170 increase
  // handler + per-bundle `freeDevice.maxQuantity` spinner):
  //  - Non-FD intervals: screens in `maxQuantityNormal`.
  //  - FD trial/first purchase: single new row `[{maxQuantity: count}]`.
  //  - FD increase on paid: keep existing rows with `_id`s, push ONE new row
  //    (no `_id`) for the delta. BE UPDATEs existing (no-op) + INSERTs the
  //    delta row, triggering a fresh coupon per purchase (audit-trail pattern).
  //  - FD decrease on paid: walk existing bundles tail→head, reducing each
  //    until the delta is absorbed. A bundle whose qty hits 0 is sent with
  //    `maxQuantity: 0 + _id` → BE REMOVE branch deletes that row. SCIO 1.0
  //    per-bundle UX lets the user pick which row to shrink; SCIO 3.0 has a
  //    single counter, so newest-first is the natural default.
  const buildQuantityParams = useCallback(
    (count: number) => {
      if (billingInterval !== BILLING_INTERVAL.YEARLY_FREEDEVICE) {
        return { maxQuantityNormal: count };
      }
      // When the user has opened the per-bundle Confirmation UI and edited
      // bundles directly, send those verbatim (SCIO 1.0 `this.deviceFrees`
      // reactive — caller owns the exact shape).
      if (editedDeviceFrees.length > 0) {
        return {
          maxQuantityNormal: 0,
          deviceFrees: editedDeviceFrees.map((b) => {
            const e: { _id?: string; maxQuantity: number } = { maxQuantity: b.maxQuantity };
            if (b._id) e._id = b._id;
            return e;
          }),
        };
      }
      if (!existingDeviceFrees.length) {
        return { maxQuantityNormal: 0, deviceFrees: [{ maxQuantity: count }] };
      }
      const existingTotal = existingDeviceFrees.reduce((sum, e) => sum + (e.maxQuantity || 0), 0);
      const delta = count - existingTotal;
      const preserved = existingDeviceFrees.map((e) => ({
        _id: e._id,
        maxQuantity: e.maxQuantity,
      }));
      if (delta > 0) {
        return {
          maxQuantityNormal: 0,
          deviceFrees: [...preserved, { maxQuantity: delta }],
        };
      }
      if (delta < 0) {
        // Walk newest→oldest, absorbing the reduction. A bundle reduced to 0
        // keeps its `_id` so BE's REMOVE branch fires and cleans up the row
        // (and the associated coupon via processDeviceFreesCrud).
        let remaining = -delta;
        const adjusted = [...preserved]
          .reverse()
          .map((entry) => {
            if (remaining <= 0) return entry;
            const take = Math.min(remaining, entry.maxQuantity);
            remaining -= take;
            return { _id: entry._id, maxQuantity: entry.maxQuantity - take };
          })
          .reverse();
        return { maxQuantityNormal: 0, deviceFrees: adjusted };
      }
      // delta === 0: no change, send existing as-is.
      return { maxQuantityNormal: 0, deviceFrees: preserved };
    },
    [billingInterval, existingDeviceFrees, editedDeviceFrees],
  );

  /**
   * Initialize per-bundle editable state for the Confirmation step. Should be
   * called when the user enters the Confirmation step on yearly_freedevice.
   * Safe to call multiple times: the second call re-seeds only if the scenario
   * identity (screen count + existing bundle hash) changed.
   */
  const initEditedDeviceFrees = useCallback(() => {
    if (billingInterval !== BILLING_INTERVAL.YEARLY_FREEDEVICE) return;
    const identity = JSON.stringify({
      screenCount,
      bundles: existingDeviceFrees.map((e) => [e._id, e.maxQuantity]),
    });
    if (editedBundlesInitRef.current === identity) return;
    editedBundlesInitRef.current = identity;

    const bundles: EditableBundle[] = existingDeviceFrees.map((e) => ({
      _id: e._id,
      maxQuantity: e.maxQuantity,
      maxQuantityOption: e.maxQuantity, // SCIO 1.0: existing = decrease only
      expiredDate: e.expiredDate,
    }));
    const currentTotal = bundles.reduce((s, b) => s + b.maxQuantity, 0);
    const delta = screenCount - currentTotal;
    if (delta > 0) {
      bundles.push({ maxQuantity: delta, maxQuantityOption: 9999 });
    } else if (delta < 0) {
      let remaining = -delta;
      for (let i = bundles.length - 1; i >= 0 && remaining > 0; i--) {
        const take = Math.min(remaining, bundles[i].maxQuantity);
        bundles[i].maxQuantity -= take;
        remaining -= take;
      }
    }
    setEditedDeviceFrees(bundles);
  }, [billingInterval, screenCount, existingDeviceFrees]);

  /**
   * Update a single bundle's quantity in the per-bundle view.
   * Constraint (SCIO 1.0): `qty` must be in [0, maxQuantityOption].
   * Also keeps `screenCount` synced to the sum so the Summary sidebar stays correct.
   */
  const setBundleQuantity = useCallback((index: number, qty: number) => {
    setEditedDeviceFrees((prev) => {
      if (!prev[index]) return prev;
      const cap = prev[index].maxQuantityOption;
      const clamped = Math.max(0, Math.min(cap, qty));
      const next = [...prev];
      next[index] = { ...next[index], maxQuantity: clamped };
      // Mirror the new total back into screenCount so the Summary sidebar and
      // price calculations in WizardStepConfirmation stay in sync.
      const total = next.reduce((s, b) => s + b.maxQuantity, 0);
      setScreenCount(total);
      return next;
    });
  }, []);

  const addonItems = useMemo(() => {
    if (!paymentAddons?.length) return [];
    const addOns = (aliasData as Record<string, unknown>)?.add_ons as
      | Record<string, Record<string, unknown>>
      | undefined;
    if (!addOns) return [];
    const items: Array<{
      key: string;
      name: string;
      pricePerUnit: number;
      quantity: number;
      total: number;
      planFeature?: string;
    }> = [];
    for (const sub of paymentAddons) {
      if (!sub.isActive || sub.isTrial) continue;
      // Upgrading TO the Engage plan makes a standalone Engage add-on redundant: on commit it's
      // cancelled + credited (the plan covers Engage account-wide), and the BE preview mirrors that.
      // Drop it from the summary so the line item AND clientSubtotal/subTotal match the actual charge
      // (else the wizard shows a phantom "Engage $20 / 1 screen" line + an inflated Due Today). B19.
      if (isEngageAddonRedundantOnPlan(sub.key, selectedPlan)) continue;
      const addonPricing = addOns[sub.key] as Record<string, unknown> | undefined;
      const name = (addonPricing?.name as string) ?? sub.key;
      const priceMap = addonPricing?.price as
        | Record<string, Record<string, { price?: number }>>
        | undefined;
      // scio1 parity (subscriptionPlanModal.js:789): tiered addons use
      // `{tier}_{interval}` as the price key (e.g. `pro_monthly`) except
      // Standard which uses the plain interval key. Default to 'standard'
      // when the backend hasn't persisted a planFeature on the subscription.
      const tierCatalog = ADDON_TIER_CATALOG[sub.key];
      const effectiveTier = tierCatalog ? (sub.planFeature ?? 'standard') : sub.planFeature;
      let priceKey: string = billingInterval;
      if (tierCatalog && effectiveTier) {
        const tierEntry = tierCatalog.find((t) => t.tier === effectiveTier);
        if (tierEntry) {
          priceKey =
            billingInterval === BILLING_INTERVAL.YEARLY
              ? tierEntry.priceKeyYearly
              : tierEntry.priceKeyMonthly;
        }
      }
      const price = priceMap?.[effectiveCurrency]?.[priceKey]?.price ?? 0;
      // OptiDev with enableAllScreens: backend auto-syncs OptiDev quantity to the
      // new screen count when this charge commits, so preview the synced value here
      // instead of the stale stored maxQuantity. Floor 3 (OptiDev minQuantity).
      const quantity =
        sub.key === 'optidev' && sub.enableAllScreens && !sub.isTrial
          ? Math.max(screenCount, 3)
          : (sub.maxQuantity ?? 0);
      items.push({
        key: sub.key,
        name,
        pricePerUnit: price,
        quantity,
        total: price * quantity,
        planFeature: effectiveTier,
      });
    }
    return items;
  }, [paymentAddons, aliasData, effectiveCurrency, billingInterval, screenCount, selectedPlan]);

  const addonTotal = useMemo(() => {
    return addonItems.reduce((sum, item) => sum + item.total, 0);
  }, [addonItems]);

  const planName = aliasData?.name ?? getPlanDisplayName(currentPlanFeature ?? 'free');
  const planInfoText =
    !isTrial && currentPlanFeature !== PLAN_FEATURES.FREE_PLAN
      ? `${t('Your Plan')}: ${planName} ${effectiveCurrencySymbol}${currentPricePerScreen.toFixed(2)}/screen/${billingInterval === BILLING_INTERVAL.YEARLY ? t('year') : t('month')}`
      : undefined;

  // ---- Upcoming review (step 4) ----
  const [fetchReview, { data: reviewData, loading: reviewLoading }] = useLazyQuery(
    GET_UPCOMING_SUBSCRIPTION_REVIEW,
    { fetchPolicy: 'network-only' },
  );

  const reviewResult = useMemo(() => {
    const raw = reviewData as Record<string, unknown> | undefined;
    if (!raw?.getUpcomingSubscriptionReview) return null;
    return raw.getUpcomingSubscriptionReview as UpcomingSubscriptionReviewData;
  }, [reviewData]);

  // ---- Stripe setup ----
  const [fetchStripeElement, { data: stripeData, loading: stripeLoading }] = useLazyQuery(
    STRIPE_BUILD_ELEMENT,
    { fetchPolicy: 'network-only' },
  );

  const stripeClientSecret = useMemo(() => {
    const raw = stripeData as Record<string, unknown> | undefined;
    const result = raw?.stripeBuildElement as Record<string, unknown> | undefined;
    return (result?.client_secret as string) ?? null;
  }, [stripeData]);

  const stripePaymentMethodOrder = useMemo(() => {
    const raw = stripeData as Record<string, unknown> | undefined;
    const result = raw?.stripeBuildElement as Record<string, unknown> | undefined;
    return (result?.paymentMethodOrder as string[]) ?? [];
  }, [stripeData]);

  // Re-fetch stripe element to get a fresh setup intent after charge failure.
  // When confirmSetup succeeds but handleCharge fails (e.g. insufficient funds),
  // the old setup intent is consumed — Elements needs a new one to retry.
  const refreshStripeSetup = useCallback(() => {
    fetchStripeElement({
      variables: {
        form: 'SUBSCRIPTION',
        currency: effectiveCurrency,
        subAccountId: contextSubAccountId,
        teamId: contextTeamId,
      } satisfies StripeBuildElementVariables,
    });
  }, [fetchStripeElement, effectiveCurrency, contextSubAccountId, contextTeamId]);

  // ---- Mutations ----
  const [chargeSubscription] = useMutation(CHARGE_SUBSCRIPTION_USER);
  // Used to write a `cancelSwitchPlans` userSurveys row when a plan-update
  // initiated from the cancel-subscription dialog actually completes here.
  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);

  /**
   * Records a `cancelPlanChange` survey row IFF the wizard was opened from
   * the cancel-subscription dialog AND a plan-update just succeeded. The
   * cancel_reason question (switch_plans vs too_expensive) tells reviewers
   * which path the user took — switch vs downgrade — so a single formType
   * covers both. Fire-and-forget; clears the source after writing.
   */
  // useCallback so the helper has stable identity across renders — lets the
  // useCallback handlers that call it list it as a dep without churning on
  // every render.
  const recordCancelFlowPlanSwitch = useCallback(
    (params: {
      planId: string;
      currency: string;
      interval: 'yearly' | 'monthly';
      pricePerScreen: number;
    }) => {
      if (!cancelFlowSource) return;
      const reasonAnswer = cancelFlowSource;
      const reasonLabel =
        cancelFlowSource === 'switch_plans' ? 'I want to switch plans' : "It's too expensive";
      setCancelFlowSource(null);
      const questions: SubmitUserSurveyVariables['input']['questions'] = [
        {
          key: 'cancel_reason',
          text: 'Why are you cancelling?',
          answer: reasonAnswer,
          answerLabel: reasonLabel,
        },
        {
          key: 'new_plan_id',
          text: 'New plan (Stripe price ID)',
          answer: params.planId,
          answerLabel: params.planId,
        },
        {
          key: 'new_billing_interval',
          text: 'New billing interval',
          answer: params.interval,
          answerLabel: params.interval,
        },
        {
          key: 'new_plan_price_per_screen',
          text: 'New plan price per screen',
          answer: String(params.pricePerScreen),
          answerLabel: `${params.currency.toUpperCase()} ${params.pricePerScreen.toFixed(2)}`,
        },
      ];
      void submitSurvey({
        variables: {
          input: {
            // formId is the form (cancel-sub dialog); formType is the outcome.
            formId: 'cancelSubscription',
            formVersion: 1,
            formType: 'cancelPlanChange',
            questions,
          },
        },
      }).catch((err) => {
        console.error('submitUserSurvey (cancelPlanChange) failed', err);
      });
    },
    [cancelFlowSource, setCancelFlowSource, submitSurvey],
  );
  // Enterprise / isSyncPriceStripe path — Stripe owns pricing; we only push
  // the new maxQuantityNormal. SCIO 1.0 parity: subscriptionPlanModal.js:1085
  // (setStep short-circuit) + getInputCharge():1284 (only maxQuantityNormal).
  const [chargeSubscriptionSpecial] = useMutation(CHARGE_SUBSCRIPTION_SPECIAL);
  // Issue #49 — team-billing flow also persists Team Name + Purpose edits
  // through the wizard. Called before the subscription charge so that if the
  // rename fails we don't end up charging for a mis-named team.
  const [updateTeam] = useMutation(UPDATE_TEAM);

  const [applyPromoCode] = useMutation(UPDATE_PROMO_CODE_SUBSCRIPTION);

  // Trial → Free downgrade mutation now lives in DowngradeComparisonDialog (which fires it
  // when a trial user confirms the comparison). Paid → Free routes through DowngradeDialog
  // as before.
  const [fetchCoupon] = useLazyQuery(GET_COUPON_STRIPE, { fetchPolicy: 'network-only' });
  const [fetchValidateChangePlan] = useLazyQuery(VALIDATE_CHANGE_PLAN, {
    fetchPolicy: 'network-only',
  });
  const [fetchUpcoming] = useLazyQuery(GET_UPCOMING_SUBSCRIPTION, { fetchPolicy: 'network-only' });
  // #1460 — check member count before the downgrade survey for trial accounts.
  const [fetchMembersForDowngrade] = useLazyQuery<{
    getCompanyInfoWithUserMfa: {
      activatedInvitedUserCount?: number | null;
      users?: { id: string }[];
    } | null;
  }>(GET_COMPANY_WITH_MEMBERS, { fetchPolicy: 'network-only' });

  const useExistingCard = useExistingCardOverride ?? !!existingCard;

  // ---- Step navigation ----
  const stepIndex = wizardSteps.indexOf(step);
  const planFeatureId = aliasData?._id;

  // Billing address from AddressElement — captured for redirect flows (iDEAL/Bancontact).
  // Included in stripeReturnUrl so PaymentIntentPage can pass it to backend after redirect.
  const [billingAddress, setBillingAddress] = useState<Record<string, string> | null>(null);
  const handleBillingAddressChange = useCallback((address: Record<string, string> | null) => {
    setBillingAddress(address);
  }, []);

  // ---- Stripe return URL with encoded charge params (survives redirect) ----
  const stripeReturnUrl = useMemo(() => {
    const chargeData: Record<string, unknown> = {
      planId: getPlanIdForGraphQL(billingInterval),
      planFeatureId: planFeatureId ?? selectedPlan,
      ...buildQuantityParams(screenCount),
      currency: effectiveCurrency,
      promoCode: promoCode ?? undefined,
      subAccountId: contextSubAccountId,
      teamId: contextTeamId,
    };
    // Include billing address for redirect flows (iDEAL/Bancontact) so
    // PaymentIntentPage can pass it to backend to update Stripe customer address
    if (billingAddress) {
      chargeData.billingAddress = billingAddress;
    }
    // Include finish data for the Thank You page after redirect
    chargeData.finishAction = isTrial
      ? 'trial'
      : maxScreens < screenCount
        ? 'increase'
        : 'decrease';
    chargeData.planName = aliasData?.name ?? getPlanDisplayName(selectedPlan);
    chargeData.billingCycle = BILLING_INTERVAL_LABELS[billingInterval];
    chargeData.pricePerScreen = currentPricePerScreen;
    chargeData.addonTotal = addonTotal;
    const encoded = btoa(JSON.stringify(chargeData));
    return `${window.location.origin}/app/s/payment-intent?charge=${encodeURIComponent(encoded)}`;
  }, [
    billingInterval,
    buildQuantityParams,
    planFeatureId,
    selectedPlan,
    screenCount,
    effectiveCurrency,
    promoCode,
    contextSubAccountId,
    contextTeamId,
    billingAddress,
    isTrial,
    maxScreens,
    aliasData?.name,
    currentPricePerScreen,
    addonTotal,
  ]);

  /** Fetch review + stripe data for the confirmation step */
  const fetchConfirmationData = useCallback(
    (opts?: { includeAddonTotal?: boolean }) => {
      const reviewVars: UpcomingSubscriptionReviewVariables = {
        params: {
          planId: getPlanIdForGraphQL(billingInterval),
          planFeatureId: planFeatureId ?? selectedPlan,
          ...buildQuantityParams(screenCount),
          subTotal:
            screenCount * currentPricePerScreen + (opts?.includeAddonTotal ? addonTotal : 0),
          proration_date: Math.floor(Date.now() / 1000),
          subAccountId: contextSubAccountId,
          teamId: contextTeamId,
        },
      };
      fetchReview({ variables: reviewVars });

      const stripeVars: StripeBuildElementVariables = {
        form: 'SUBSCRIPTION',
        currency: effectiveCurrency,
        subAccountId: contextSubAccountId,
        teamId: contextTeamId,
      };
      fetchStripeElement({ variables: stripeVars });
    },
    [
      billingInterval,
      buildQuantityParams,
      selectedPlan,
      screenCount,
      contextSubAccountId,
      contextTeamId,
      fetchReview,
      fetchStripeElement,
      addonTotal,
      effectiveCurrency,
      currentPricePerScreen,
      planFeatureId,
    ],
  );

  /** Wrapper for setScreenCount that re-fetches prorated review when on the Confirmation step.
   * This ensures the Total price recalculates dynamically when users change screen count.
   * The review fetch is debounced (500ms) to avoid excessive API calls from rapid +/- clicks. */
  const reviewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (reviewDebounceRef.current) clearTimeout(reviewDebounceRef.current);
    };
  }, []);
  const handleScreenCountChange = useCallback(
    (newCount: number) => {
      setScreenCount(newCount);
      // Re-fetch prorated review when changing screen count on the Confirmation step
      if (step === STEP.CONFIRMATION && planFeatureId) {
        if (reviewDebounceRef.current) clearTimeout(reviewDebounceRef.current);
        reviewDebounceRef.current = setTimeout(() => {
          const reviewVars: UpcomingSubscriptionReviewVariables = {
            params: {
              planId: getPlanIdForGraphQL(billingInterval),
              planFeatureId: planFeatureId ?? selectedPlan,
              ...buildQuantityParams(newCount),
              subTotal: newCount * currentPricePerScreen + addonTotal,
              proration_date: Math.floor(Date.now() / 1000),
              subAccountId: contextSubAccountId,
              teamId: contextTeamId,
            },
          };
          fetchReview({ variables: reviewVars });
        }, 500);
      }
    },
    [
      step,
      billingInterval,
      buildQuantityParams,
      selectedPlan,
      planFeatureId,
      currentPricePerScreen,
      addonTotal,
      contextSubAccountId,
      contextTeamId,
      fetchReview,
    ],
  );

  /** Core step advancement. Validates plan change when leaving PLAN step (unless skipped
   * after user confirmed the warning dialog). This is async because validation requires
   * an API call — the step transition only happens after validation resolves. */
  const doGoNext = useCallback(async () => {
    // Block re-entrant calls while validation is in-flight
    if (validatingPlanRef.current) return;

    // When leaving PLAN step, validate plan change (downgrade warnings)
    // Skip if user already confirmed the warning via confirmChangePlanWarning
    if (
      !skipNextValidationRef.current &&
      step === STEP.PLAN &&
      selectedPlan !== currentPlanFeature
    ) {
      validatingPlanRef.current = true;
      setValidatingPlan(true);
      try {
        const { data } = await fetchValidateChangePlan({
          variables: { planAlias: selectedPlan, subAccountId: contextSubAccountId },
        });
        const result = (data as Record<string, unknown>)?.validateChangePlan as
          | { warnings?: Array<Record<string, unknown>> }
          | undefined;
        const warnings = result?.warnings;
        if (warnings && Array.isArray(warnings) && warnings.length > 0) {
          // Expired/cancelled flow (fromInactive): system is locked so the user cannot
          // act on "Go To account members" or a feature list (#1495).
          // - Always strip warningFeatures (non-actionable when account is locked).
          // - Strip url + rewrite message for any blocking (requiredRemove) warning.
          // - If nothing actionable remains after filtering, skip the dialog and proceed.
          const filteredWarnings = fromInactive
            ? warnings
                .filter((w) => w.featureType !== 'warningFeatures')
                .map((w) =>
                  w.requiredRemove
                    ? {
                        ...w,
                        url: undefined,
                        // Pre-translate so dialog never shows a raw key on i18n failure (#1495).
                        // defaultValue ensures readable English text if CDN translation not yet loaded.
                        messageDisplay: t('TEAMS_NOT_SUPPORTED_UPGRADE_REQUIRED', {
                          plan: String(w.plan ?? ''),
                          defaultValue: `The ${String(w.plan ?? '')} Plan does not support teams. Please upgrade to Pro Plus or higher to continue.`,
                        }),
                      }
                    : w,
                )
            : warnings;
          if (filteredWarnings.length > 0) {
            setChangePlanWarnings(filteredWarnings);
            setShowChangePlanWarning(true);
            return; // Block — user must confirm or dismiss the warning
          }
          // filteredWarnings empty: all were informational-only; fall through to proceed
        }

        // Synthesize teams warning for cancelled accounts where backend may miss it (#1495).
        // When payment.planFeature is null/stale after cancellation, backend defaults
        // currentPlan to 'standard' → misses the allowTeams check entirely.
        // #1655 — gate on actual team existence (hasCreatedTeams), NOT the allowTeams
        // capability flag alone. allowTeams stays true after trial-expiry / hard Stripe
        // cancel (only an app-side downgrade resets it), so an expired-trial account with
        // zero created teams was wrongly blocked and — since the trial-expired modal blocks
        // all other access — locked out entirely. Mirrors the non-inactive branch below.
        const PLANS_WITHOUT_TEAMS = ['free', 'standard'];
        if (
          fromInactive &&
          companyFeature?.allowTeams === true &&
          hasCreatedTeams &&
          PLANS_WITHOUT_TEAMS.includes(selectedPlan)
        ) {
          const planDisplayName = aliasData?.name ?? getPlanDisplayName(selectedPlan);
          setChangePlanWarnings([
            {
              requiredRemove: true,
              url: undefined,
              messageDisplay: t('TEAMS_NOT_SUPPORTED_UPGRADE_REQUIRED', {
                plan: planDisplayName,
                defaultValue: `The ${planDisplayName} Plan does not support teams. Please upgrade to Pro Plus or higher to continue.`,
              }),
              plan: planDisplayName,
            },
          ]);
          setShowChangePlanWarning(true);
          return;
        }
      } catch {
        // If backend call fails, proceed without blocking
      } finally {
        validatingPlanRef.current = false;
        setValidatingPlan(false);
      }
    }
    skipNextValidationRef.current = false;

    const nextIndex = stepIndex + 1;
    if (nextIndex < wizardSteps.length) {
      const nextStep = wizardSteps[nextIndex];

      if (nextStep === STEP.CONFIRMATION) {
        fetchConfirmationData({ includeAddonTotal: true });
      }

      setStep(nextStep);
    }
  }, [
    step,
    stepIndex,
    wizardSteps,
    selectedPlan,
    currentPlanFeature,
    contextSubAccountId,
    fetchValidateChangePlan,
    fetchConfirmationData,
    fromInactive,
    companyFeature,
    hasCreatedTeams,
    aliasData,
    t,
  ]);

  /** Advance to the next wizard step. Calls doGoNext which handles validation internally. */
  const goNext = useCallback(() => {
    doGoNext();
  }, [doGoNext]);

  /**
   * Cancel-flow handoff: user picked "It's too expensive" → "Switch to Standard"
   * (or any plan) in the cancel-subscription dialog. Skip the regular plan-change
   * validation/warning pop-up (the cancel dialog surfaces those losses inline on
   * its retention offer instead) and jump straight to the wizard's Confirmation
   * step. The pending flag lets goNext run on the next render — by then
   * `selectedPlan` has propagated into doGoNext's closure.
   */
  const [pendingPlanSwitchAdvance, setPendingPlanSwitchAdvance] = useState(false);
  const switchPlanFromCancelFlow = useCallback(
    (planAlias: string) => {
      if (planAlias === currentPlanFeature) return;
      setSelectedPlan(planAlias);
      setStep(STEP.PLAN);
      setPendingPlanSwitchAdvance(true);
    },
    [currentPlanFeature],
  );
  useEffect(() => {
    if (pendingPlanSwitchAdvance && step === STEP.PLAN && selectedPlan !== currentPlanFeature) {
      setPendingPlanSwitchAdvance(false);
      // Skip the next PLAN-step validation — the cancel dialog already showed
      // (or offered to show) the feature-loss warnings inline before this handoff.
      skipNextValidationRef.current = true;
      goNext();
    }
  }, [pendingPlanSwitchAdvance, step, selectedPlan, currentPlanFeature, goNext]);

  const goBack = useCallback(() => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setStep(wizardSteps[prevIndex]);
    }
  }, [stepIndex, wizardSteps]);

  // Bug 6 — holds the cancel() for the in-flight referral-prompt poll so we can
  // abort it (no unbounded background work). The poll is STARTED at the moment
  // the wizard closes from FINISH, so it must outlive that close — it is bounded
  // (~3 attempts / ~5s) and only cancelled on unmount or when a fresh sync
  // supersedes it (see syncMeIntoStore). The referral widget that reads the
  // synced prompt lives in the AppShell, not in this dialog.
  const referralSyncCancelRef = useRef<(() => void) | null>(null);
  const cancelReferralSync = useCallback(() => {
    referralSyncCancelRef.current?.();
    referralSyncCancelRef.current = null;
  }, []);
  // Cancel any pending poll on unmount only.
  useEffect(() => cancelReferralSync, [cancelReferralSync]);

  /**
   * Bug 6 — re-sync `me` into the auth-store after a finish, then bounded-poll
   * for the (webhook-driven) `referralPromptPending` ('trial' → first_paid;
   * any paid expansion → 'expansion'). Runs for EVERY finish: finishAction is
   * computed from the SCREEN count delta only (trial/increase/decrease), so a
   * plan upgrade with unchanged screens reports 'decrease' — the old
   * trial/increase gate made the expansion modal miss plan upgrades entirely.
   * The poll is bounded (3 × ME_QUERY over ~5s) and stops early, so running it
   * on a true decrease is a harmless no-op (backend sets no prompt there).
   * Any prior poll is cancelled first so we never stack overlapping polls.
   */
  const syncMeIntoStore = useCallback(() => {
    cancelReferralSync();
    referralSyncCancelRef.current = syncMeForReferralPrompt({
      apolloClient,
      setMe,
      getPendingPrompt: () => useAuthStore.getState().me?.referralPromptPending,
    });
  }, [apolloClient, setMe, cancelReferralSync]);

  // ---- Reset on open/close ----
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isSubmitting) return;
      if (!open) {
        // Paywall recovery: if user is closing AFTER a successful charge while
        // me.accountStatus === false, hard-reload so the paywall lifts and every
        // Apollo query, Zustand store, and company-scoped cache picks up fresh data.
        // Mirrors scio1's $state.go after subscriptionInitThankYou / paymentThankYou.
        // (No referral re-sync needed here — the reload re-reads `me` fresh.)
        if (isAccountInactive && chargeResult) {
          closeDialog();
          window.location.reload();
          return;
        }
        // Bug 6 — closing from the FINISH step is the post-pay "Continue".
        // Re-sync `me` into the store + bounded-poll so the referral widget
        // opens right away instead of only after a refresh. Every finish is
        // eligible (plan upgrades report finishAction 'decrease' — see
        // syncMeIntoStore); true decreases are a harmless bounded no-op.
        if (step === STEP.FINISH) {
          syncMeIntoStore();
        }
        closeDialog();
        setTimeout(() => {
          setStep(STEP.NUMBER_SCREEN);
          // Reset to current billing data — useBillingData will provide correct values on next open
          // Trial accounts reset to 1 (minimum purchase), matching scio1 behavior
          setScreenCount(isTrial ? 1 : maxScreens || 1);
          setSelectedPlan(defaultSelectedPlan);
          setBillingInterval(initialInterval);
          setCurrencyOverride(null);
          setUseExistingCard(null);
          setIsSubmitting(false);
          // #1117 — also reset stripeProcessing on close. Without this, a
          // previous failed Stripe validation leaves the flag stuck at true,
          // and the wizard footer button shows "Processing..." immediately
          // when the user reopens the wizard.
          setStripeProcessing(false);
          setPromoCode(null);
          setDiscountLabel(null);
          setChargeResult(null);
          setSkippedToConfirmation(false);
        }, 200);
      }
    },
    [
      closeDialog,
      maxScreens,
      defaultSelectedPlan,
      isSubmitting,
      initialInterval,
      isTrial,
      isAccountInactive,
      chargeResult,
      step,
      syncMeIntoStore,
    ],
  );

  // ---- Promo code ----
  const handleApplyPromo = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        await applyPromoCode({
          variables: {
            promoCode: code,
            subAccountId: contextSubAccountId,
            teamId: contextTeamId,
          },
        });
        const { data: couponData } = await fetchCoupon();
        const couponRaw = couponData as Record<string, unknown> | undefined;
        const coupon = couponRaw?.getCouponStripe as Record<string, unknown> | undefined;
        if (coupon) {
          setPromoCode(code);
          const percentOff = coupon.percent_off as number | undefined;
          const amountOff = coupon.amount_off as number | undefined;
          if (percentOff) {
            setDiscountLabel(`${percentOff}% ${t('off')}`);
          } else if (amountOff) {
            setDiscountLabel(`${formatCurrencyAmount(amountOff / 100)} ${t('off')}`);
          } else {
            setDiscountLabel(t('Applied'));
          }
          return true;
        }
        return false;
      } catch {
        return false;
      }
    },
    [applyPromoCode, fetchCoupon, contextSubAccountId, contextTeamId, t],
  );

  const handleRemovePromo = useCallback(() => {
    setPromoCode(null);
    setDiscountLabel(null);
  }, []);

  // ---- Charge ----
  const handleCharge = useCallback(
    async (setupIntentId?: string) => {
      // Detect any team-name or description edit — for team context users may
      // be updating only metadata without a screen/plan change. Allow flow
      // through the rename-only path (Issue #49).
      const nameChanged =
        !!contextTeamId && (teamNameInput ?? '').trim() !== (currentTeamName ?? '').trim();
      const descChanged =
        !!contextTeamId &&
        (teamDescriptionInput ?? '').trim() !== (currentTeamDescription ?? '').trim();
      const teamFieldsChanged = nameChanged || descChanged;
      const billingFieldsChanged =
        isTrial ||
        screenCount !== maxScreens ||
        selectedPlan !== currentPlanFeature ||
        billingInterval !== initialInterval;

      if (!billingFieldsChanged && !teamFieldsChanged) {
        toast(t('No changes detected. Your subscription remains the same.'));
        // The SetupIntent was just consumed by StripeCardForm's confirmSetup
        // before handleCharge ran. Without a refresh here, a subsequent submit
        // (e.g. user increases screens then retries) reuses the spent
        // clientSecret and Stripe rejects with "A processing error occurred.".
        // Mirrors the refresh on every charge-failure branch below
        // (ZIPCODE_ISSUE / ROLLBACK / generic else). See
        // claude-dev/bugfix-anhluuquoc/2026-05-07-no-change-stripe-stale/.
        refreshStripeSetup();
        return;
      }

      setIsSubmitting(true);
      try {
        // Team context: persist Team Name + Purpose edits before charging.
        // Matches scio1 teamModal update-subscription flow. Backend
        // `TeamService.saveTeam` (team.service.ts:29) spreads input into
        // Mongoose `$set` but explicitly passes `'limitation.maxQuantity':
        // maxQuantity` even when undefined — so omitting it from the payload
        // zeros the team's screen quota. Always send all three
        // (name, description, limitation.maxQuantity) so a rename-only edit
        // doesn't silently wipe existing values.
        if (contextTeamId && teamFieldsChanged) {
          try {
            await updateTeam({
              variables: {
                input: {
                  _id: contextTeamId,
                  name: (teamNameInput ?? currentTeamName ?? '').trim(),
                  description: (teamDescriptionInput ?? currentTeamDescription ?? '').trim(),
                  limitation: { maxQuantity: maxScreens },
                },
              },
              refetchQueries: ['GetTeamBillingInfo', 'getAllTeams', 'teamsPaginated'],
            });
          } catch {
            toast.error(t('Failed to update team details'));
            setIsSubmitting(false);
            return;
          }
        }

        // Metadata-only update — no billing change, skip Stripe charge entirely.
        // Without this, Stripe returns a proration figure and the Thank-you
        // screen shows a bogus "Total paid today for changes" amount.
        if (!billingFieldsChanged) {
          toast.success(t('Team details updated successfully'));
          setIsSubmitting(false);
          closeDialog();
          return;
        }

        if (promoCode) {
          try {
            await applyPromoCode({
              variables: {
                promoCode,
                subAccountId: contextSubAccountId,
                teamId: contextTeamId,
              },
            });
          } catch {
            toast.error(t('Invalid promo code'));
            setIsSubmitting(false);
            return;
          }
        }

        // Enterprise / custom-pricing short-circuit — only maxQuantityNormal
        // is mutable; plan/interval/price stay sourced from Stripe via the
        // BE chargeSubscriptionSpecial method. scio1 parity:
        // subscriptionPlanModal.js:1085 → updateSubscriptionSpecial.
        if (isSyncPriceStripe) {
          const refetchListSpecial = contextSubAccountId
            ? [
                'Me',
                'GetCompanyInfo',
                'GetSubAccountInfo',
                'GetSubAccountList',
                'GetSubscriptionInfoStripe',
                'GetUpcomingSubscription',
              ]
            : ['Me', 'GetCompanyInfo', 'GetSubscriptionInfoStripe', 'GetUpcomingSubscription'];
          const specialResult = await chargeSubscriptionSpecial({
            variables: {
              data: {
                maxQuantityNormal: screenCount,
                subAccountId: contextSubAccountId ?? undefined,
              },
            },
            refetchQueries: refetchListSpecial,
          });
          const specialResponse = extractMutationResponse<Record<string, unknown>>(
            specialResult.data,
            'chargeSubscriptionSpecial',
          );
          if (specialResponse?.status) {
            // GTM revenue event — enterprise/sync-price path is always an update (never first).
            // scio1 parity: subscriptionPlanModal.js:sendAnalyticsTracking.
            // planID must be the Stripe plan ID (not a GraphQL interval enum) so GTM tags
            // filtering on `plan_*` continue to match.
            analyticsSubscriptionUpdated({
              planId: aliasPlanData?.[effectiveCurrency]?.[billingInterval]?.stripe_plan_id ?? '',
              noOfDevices: screenCount - maxScreens,
              screenCount: screenCount,
              currency: effectiveCurrency,
              stripeDuration: billingInterval === BILLING_INTERVAL.YEARLY ? 'yearly' : 'monthly',
              stripePlanPrice: currentPricePerScreen,
              stripeDiscount: 0,
              purchaseSource: contextPurchaseSource,
            });
            recordCancelFlowPlanSwitch({
              planId: aliasPlanData?.[effectiveCurrency]?.[billingInterval]?.stripe_plan_id ?? '',
              currency: effectiveCurrency,
              interval: billingInterval === BILLING_INTERVAL.YEARLY ? 'yearly' : 'monthly',
              pricePerScreen: currentPricePerScreen,
            });
            toast.success(t('Subscription updated successfully'));
            closeDialog();
          } else if (
            specialResponse?.code === PAYMENT_STATUS_CODE.LIMIT_QUANTITY &&
            specialResponse?.message === 'limitPurchaseDeviceExcess'
          ) {
            // #1136 — User asked to set quantity below current paired count.
            // Open Screen Management overlay (existing component from SP53) so they can
            // move excess devices to OnHold before retrying. Wizard stays open behind.
            // #1244 — propagate teamId so the popup scopes to the right billing
            // context: single-team when the wizard is in team-billing mode,
            // cross-team when in company-billing mode.
            openScreenManagement({
              subAccountId: contextSubAccountId,
              teamId: contextTeamId,
              messageKey: 'screenManagementModal1',
            });
            setIsSubmitting(false);
            // #1232 — also clear stripeProcessing so the wizard footer button isn't stuck on
            // "Processing..." while the user moves screens to OnHold in the overlay and returns.
            setStripeProcessing(false);
            return;
          } else {
            const message =
              (specialResponse?.message as string) ?? t('Payment failed. Please try again.');
            toast.error(message);
          }
          setIsSubmitting(false);
          return;
        }

        const chargeVars: ChargeSubscriptionVariables = {
          params: {
            planId: getPlanIdForGraphQL(billingInterval),
            planFeatureId: planFeatureId ?? selectedPlan,
            ...buildQuantityParams(screenCount),
            currency: effectiveCurrency,
            promoCode: promoCode ?? undefined,
            subAccountId: contextSubAccountId,
            teamId: contextTeamId,
            type: contextTeamId ? 'TEAM' : contextSubAccountId ? 'SUBACCOUNT' : undefined,
          },
        };
        if (setupIntentId) {
          chargeVars.params.paymentMethod = { paymentMethodId: setupIntentId };
        }
        const refetchList = contextTeamId
          ? [
              'Me',
              'GetTeamBillingInfo',
              'getAllTeams',
              'GetSubscriptionInfoStripe',
              'GetUpcomingSubscription',
            ]
          : contextSubAccountId
            ? [
                'Me',
                'GetCompanyInfo',
                'GetSubAccountInfo',
                'GetSubAccountList',
                'GetSubscriptionInfoStripe',
                'GetUpcomingSubscription',
              ]
            : ['Me', 'GetCompanyInfo', 'GetSubscriptionInfoStripe', 'GetUpcomingSubscription'];
        const result = await chargeSubscription({
          variables: chargeVars,
          // Refetch after charge — use onQueryUpdated to suppress errors (token may be stale
          // immediately after Stripe payment flow, causing 400s on refetch)
          refetchQueries: refetchList,
          onQueryUpdated(observableQuery) {
            return observableQuery.refetch().catch(() => {
              // Retry after delay — backend may need time to process subscription change
              setTimeout(() => {
                observableQuery.refetch().catch((err) => {
                  console.warn('Post-charge refetch failed:', err);
                });
              }, 2000);
              return false;
            });
          },
        });
        const response = extractMutationResponse<Record<string, unknown>>(
          result.data,
          'chargeSubscriptionUser',
        );
        if (response?.status) {
          const clientSubtotal = screenCount * currentPricePerScreen + addonTotal;

          // Prefer backend's isFinishAction over client-side recomputation
          // (Issue #51). Backend applies the correct rule set based on billing
          // cycle, Stripe invoice outcome, and delta direction; client heuristic
          // miscategorises plan-only changes and monthly decreases as 'decrease',
          // which renders the receipt card instead of the Upcoming Charge table.
          // Normalize 'increaseInvoice' → 'increase' because WizardStepFinish
          // treats them identically (both → Upcoming Charge).
          const backendFinishAction = response.isFinishAction as string | undefined;
          let finishAction: string;
          if (backendFinishAction) {
            finishAction =
              backendFinishAction === 'increaseInvoice' ? 'increase' : backendFinishAction;
          } else {
            // Fallback for backends that don't surface isFinishAction (e.g. sub-account
            // paths in older versions). Keeps legacy behavior for those flows.
            if (isTrial) finishAction = 'trial';
            else if (maxScreens < screenCount) finishAction = 'increase';
            else finishAction = 'decrease';
          }

          // scio1 parity for team billing (Issue #51): teamModal update-subscription
          // flow ALWAYS shows the Upcoming Charge table regardless of whether the
          // change was an increase, decrease, or plan-only edit — the admin needs
          // the prorated line-item breakdown + next-charge date in the same dialog.
          // Only exception is trial (new team never lands here but keep for safety).
          if (contextTeamId && finishAction !== 'trial') {
            finishAction = finishAction === 'decrease' ? 'decreaseNotRefund' : finishAction;
          }

          const baseResult: Record<string, unknown> = {
            finishAction,
            email: defaultEmail,
            planName: aliasData?.name ?? getPlanDisplayName(selectedPlan),
            billingCycle: BILLING_INTERVAL_LABELS[billingInterval],
            billingCycleText:
              (aliasPlanData?.[effectiveCurrency]?.[billingInterval]?.text as string) ?? '',
            pricePerScreen: currentPricePerScreen,
            screens: screenCount,
            subtotal: clientSubtotal,
            credit: 0,
            total: clientSubtotal,
            totalPaidToday: clientSubtotal,
            cardBrand: existingCard?.brand,
            cardLast4: existingCard?.last4,
            addonItems,
          };

          if (finishAction === 'increase') {
            // Subtitle shows the plan-only monthly total (screens × pricePerScreen),
            // NOT the bundled subtotal that includes addons. The "X screens total $Y per month"
            // wording refers to the screens plan rate, not the full invoice charge.
            const planOnlyMonthlyTotal = screenCount * currentPricePerScreen;
            baseResult.summaryText = t('subscriptionPlanModal3', {
              getMaxQuantitySummary: String(screenCount),
              getCurrencyValue: effectiveCurrencySymbol,
              text: (aliasPlanData?.[effectiveCurrency]?.[billingInterval]?.text as string) ?? '',
              getSubtotalPrice: planOnlyMonthlyTotal.toFixed(2),
            });
          }

          // Always fetch upcoming after a successful charge — matches scio1's
          // initFinishPayment which is called regardless of finishAction. Reads top-level
          // `taxs` (the canonical source for VAT lines) and `credit` (in dollars).
          try {
            const { data: upcomingData } = await fetchUpcoming({
              variables: {
                isTrial: false,
                stripe_plan_id:
                  (aliasPlanData?.[effectiveCurrency]?.[billingInterval]
                    ?.stripe_plan_id as string) ?? '',
                subAccountId: contextSubAccountId,
                teamId: contextTeamId,
              },
            });
            const upcomingRaw = (upcomingData as Record<string, unknown>)
              ?.getUpcomingSubscription as Record<string, unknown> | undefined;
            if (upcomingRaw) {
              // Tax line items for the Thank-You/Receipt card AND the Upcoming
              // Charge table. Prefer NESTED `upcoming.taxs` (next-period
              // total_tax_amounts via retrieveUpcoming({ automatic_tax: enabled }))
              // — that's the value that pairs with the displayed Subtotal /
              // Est Next Bill Total. Top-level `upcomingRaw.taxs` is the
              // proration-delta tax of the just-issued invoice (a small
              // adjustment), which doesn't reconcile with the subtotal.
              // Fall back to top-level only when Stripe stripped the nested
              // field (legacy serialization edge case). See `a65fcf7d0`.
              const upcomingTaxs = (upcomingRaw.upcoming as { taxs?: unknown } | undefined)?.taxs;
              if (Array.isArray(upcomingTaxs) && upcomingTaxs.length > 0) {
                baseResult.taxs = upcomingTaxs;
              } else if (Array.isArray(upcomingRaw.taxs)) {
                baseResult.taxs = upcomingRaw.taxs;
              }
              // Backend returns credit already in dollars (customer.balance / 100)
              if (upcomingRaw.credit != null) {
                baseResult.credit = upcomingRaw.credit as number;
              }
              // Trust the backend's actual charge amount for "Total paid today for changes"
              // across ALL flows — matches scio1's `this.totalPrice = res.total`
              // (subscriptionPlanModal.js:1837). Stripe's `invoice.amount_due` is set at
              // finalization and represents the amount actually charged (NOT a running
              // balance), so this correctly reflects prorated upgrades, mid-cycle plan
              // changes, and credit applied. The hardcoded `clientSubtotal` is wrong for
              // plan upgrades (e.g. Pro Plus → Engage same screens) because it ignores
              // proration and credit from the previous plan.
              if (upcomingRaw.total != null) {
                baseResult.totalPaidToday = upcomingRaw.total as number;
              }
              // Increase / decreaseNotRefund — show the upcoming invoice table layout
              if (finishAction === 'increase' || finishAction === 'decreaseNotRefund') {
                if (upcomingRaw.upcoming) {
                  baseResult.upcoming = upcomingRaw.upcoming;
                }
              }
            }
          } catch {
            // Upcoming fetch failed, use client values
          }

          // Free device coupon — backend returns coupon code + quantity when
          // yearly_freedevice subscription creates a deviceFrees record.
          // Store in chargeResult so WizardStepFinish can offer "Order Your Free Device".
          const coupon = response.coupon as string | undefined;
          const couponNumber = response.couponNumber as number | undefined;
          if (coupon && couponNumber) {
            baseResult.orderDeviceCoupon = coupon;
            baseResult.orderDeviceCouponNumber = couponNumber;
          }

          // GTM revenue event — scio1 parity (subscriptionPlanModal.js:sendAnalyticsTracking).
          // `isTrial` snapshot reflects pre-charge state and is context-aware
          // (parent / sub-account / team) via useBillingData. planID must be the Stripe
          // plan ID (not the GraphQL interval enum) so GTM tags filtering on `plan_*` match.
          const gtmSubParams = {
            planId: aliasPlanData?.[effectiveCurrency]?.[billingInterval]?.stripe_plan_id ?? '',
            currency: effectiveCurrency,
            stripeDuration: billingInterval === BILLING_INTERVAL.YEARLY ? 'yearly' : 'monthly',
            stripePlanPrice: currentPricePerScreen,
            stripeDiscount: 0,
          };
          if (isTrial) {
            analyticsSubscriptionCreated({
              ...gtmSubParams,
              noOfDevices: screenCount,
              purchaseSource: contextPurchaseSource,
            });
          } else {
            analyticsSubscriptionUpdated({
              ...gtmSubParams,
              noOfDevices: screenCount - maxScreens,
              screenCount: screenCount,
              purchaseSource: contextPurchaseSource,
            });
            recordCancelFlowPlanSwitch({
              planId: gtmSubParams.planId,
              currency: gtmSubParams.currency,
              interval: gtmSubParams.stripeDuration as 'yearly' | 'monthly',
              pricePerScreen: gtmSubParams.stripePlanPrice,
            });
          }

          setChargeResult(baseResult);
          setStep(STEP.FINISH);
          toast.success(t('Subscription updated successfully'));
        } else {
          if (response?.code === PAYMENT_STATUS_CODE.ZIPCODE_ISSUE) {
            toast.error(
              t('Invalid Postal code. Please update your billing address in Payment Settings.'),
            );
            // Refresh setup intent so user can retry with a different card/method
            refreshStripeSetup();
          } else if (
            response?.code === PAYMENT_STATUS_CODE.LIMIT_QUANTITY &&
            response?.message === 'limitPurchaseDeviceExcess'
          ) {
            // #1136 — User asked to set quantity below current paired count.
            // Open Screen Management overlay (existing component from SP53) so they can
            // move excess devices to OnHold before retrying. Wizard stays open behind.
            // Do NOT refresh setup intent — user will retry from the same wizard.
            // #1244 — propagate teamId so the popup scopes to the right billing context.
            openScreenManagement({
              subAccountId: contextSubAccountId,
              teamId: contextTeamId,
              messageKey: 'screenManagementModal1',
            });
          } else if (response?.code === PAYMENT_STATUS_CODE.ROLLBACK) {
            // #1161 — scio1 parity (subscriptionPlanModal.js:1467-1481).
            // ROLLBACK (5556) is returned by `___revertChangeSubscription` when
            // the subscription change fails after partial state was committed —
            // the most common cause is a card decline (insufficient funds, card
            // expired, etc.). scio1 calls `enableCard()` here, swapping the
            // Confirmation step's "existing card" radio for the new-card form so
            // the user can enter a different card and retry without leaving the
            // wizard. We mirror that by flipping `useExistingCard` to false.
            const message = (response?.message as string) ?? t('Payment failed. Please try again.');
            toast.error(message);
            setUseExistingCard(false);
            refreshStripeSetup();
          } else {
            const message = (response?.message as string) ?? t('Payment failed. Please try again.');
            toast.error(message);
            // Refresh setup intent so user can retry with a different card/method
            refreshStripeSetup();
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : t('Payment failed. Please try again.');
        toast.error(message);
        // Refresh setup intent so user can retry with a different card/method
        refreshStripeSetup();
      } finally {
        setIsSubmitting(false);
        // #1232 — pair stripeProcessing reset with isSubmitting. Covers every wizard-stays-open
        // failure path after Stripe confirmSetup succeeded: LIMIT_QUANTITY, ROLLBACK, ZIPCODE_ISSUE,
        // generic catch. Otherwise the footer button stays disabled on "Processing..." indefinitely.
        setStripeProcessing(false);
      }
    },
    [
      billingInterval,
      buildQuantityParams,
      selectedPlan,
      screenCount,
      promoCode,
      contextSubAccountId,
      contextTeamId,
      contextPurchaseSource,
      chargeSubscription,
      chargeSubscriptionSpecial,
      isSyncPriceStripe,
      isTrial,
      maxScreens,
      currentPricePerScreen,
      defaultEmail,
      aliasData,
      openScreenManagement,
      existingCard,
      effectiveCurrency,
      effectiveCurrencySymbol,
      aliasPlanData,
      fetchUpcoming,
      applyPromoCode,
      t,
      addonTotal,
      addonItems,
      currentPlanFeature,
      initialInterval,
      planFeatureId,
      refreshStripeSetup,
      teamNameInput,
      teamDescriptionInput,
      currentTeamName,
      currentTeamDescription,
      updateTeam,
      closeDialog,
      recordCancelFlowPlanSwitch,
    ],
  );

  const handleConfirmExistingCard = useCallback(() => {
    handleCharge();
  }, [handleCharge]);

  const handlePaidUpdate = useCallback(() => {
    if (!planFeatureId) return;
    fetchConfirmationData();
    setSkippedToConfirmation(true);
    setStep(STEP.CONFIRMATION);
  }, [planFeatureId, fetchConfirmationData]);

  // Awaitable so callers (StripeCardForm.onSuccess wrapper in
  // WizardStepConfirmation) can reliably reset their local stripeProcessing
  // flag in a `finally` after handleCharge fully resolves — including the
  // no-changes early-return path. See
  // claude-dev/bugfix-anhluuquoc/2026-05-05-batch-04/00-init.md.
  const handleStripeSuccess = useCallback(
    async (id: string) => {
      await handleCharge(id);
    },
    [handleCharge],
  );

  const canGoNext = (() => {
    if (step === STEP.NUMBER_SCREEN && isTrial && screenCount <= 0) return false;
    // SP45 — scio1 parity: can't leave the PLAN step without a plan selected.
    // Free users open the wizard with no plan pre-selected (see selectedPlan
    // init above), so Next must stay disabled until they click a card.
    if (step === STEP.PLAN && !selectedPlan) return false;
    return true;
  })();

  // ---- Free plan / downgrade handler ----
  /**
   * Select Free now goes through a two-step overlay flow:
   *   1. DowngradeSurveyDialog — "Why are you downgrading?" reason picker. Cancelling
   *      via "Keep current plan" leaves the wizard intact.
   *   2. DowngradeComparisonDialog — Standard+ vs Free feature comparison, then:
   *        - Paid → hands off to DowngradeDialog (scio1 `freePlanDowngradeMoveToAndCancel`).
   *        - Trial → fires `downgradeTrialToFreePlan` directly (scio1 parity, no second confirm).
   *
   * Both overlays sit on top of the wizard. Explicitly NOT calling `validateChangePlan` here —
   * scio1's setStep short-circuits to onFreePlan BEFORE any validation for the Free path.
   */
  // #1275 — guard the plan cards: in a non-default team, picking a no-Team plan
  // (Free/Standard) is blocked (the user must return to the Default Team first).
  // Pro Plus / Engage keep Teams, so they pass through unchanged.
  const handleSelectPlan = useCallback(
    (plan: string) => {
      const isNoTeamPlan = plan === PLAN_FEATURES.FREE_PLAN || plan === PLAN_FEATURES.STANDARD;
      if (!isDefaultTeam && isNoTeamPlan) {
        setTeamPlanBlockOpen(true);
        return;
      }
      setSelectedPlan(plan);
    },
    [isDefaultTeam],
  );

  const handleSelectFree = useCallback(async () => {
    // #1275 — Free has no Teams; block the downgrade from inside a non-default team.
    if (!isDefaultTeam) {
      setTeamPlanBlockOpen(true);
      return;
    }
    if (selectingFreeRef.current) return;
    selectingFreeRef.current = true;
    setIsSelectingFree(true);
    try {
      // #1460 — check member count AND teams BEFORE showing the survey. Applies to
      // BOTH trial and paid-active accounts (Free plan = max 3 members, no teams) —
      // intercept before survey/comparison. The inactive/cancelled flow is handled by
      // the dedicated `fromInactive` branch below (#1495): a locked account can't act
      // on "Go to Account Members" / move-screens prompts, so it gets a different path.
      if (!fromInactive) {
        const { data } = await fetchMembersForDowngrade();
        const info = data?.getCompanyInfoWithUserMfa;
        const count = info?.activatedInvitedUserCount ?? info?.users?.length ?? 0;
        if (count > FREE_PLAN_LIMITS.MAX_USER) {
          openMemberDowngradeWarning(count);
          return;
        }
        // Free plan doesn't support teams — show blocking warning before proceeding (#1460).
        // Gate on actual team existence (hasCreatedTeams), not the allowTeams capability
        // flag, so an account with zero created teams isn't blocked. (A paid Standard
        // account has allowTeams === false, so this naturally no-ops there.)
        if (companyFeature?.allowTeams === true && hasCreatedTeams) {
          const planDisplayName = getPlanDisplayName('free');
          setChangePlanWarnings([
            {
              requiredRemove: true,
              url: undefined,
              messageDisplay: t('DOWNGRADE_TEAMS_WARNING', {
                plan: planDisplayName,
                defaultValue: `The ${planDisplayName} Plan does not support teams. To downgrade to ${planDisplayName}, first move all screens and content to the Default team and delete created teams.`,
              }),
              plan: planDisplayName,
            },
          ]);
          setShowChangePlanWarning(true);
          return;
        }
      }
      // Expired/cancelled flow: Free plan doesn't support teams (#1495).
      // handleSelectFree bypasses validateChangePlan, so synthesize the warning here.
      // #1655 — gate on hasCreatedTeams, not allowTeams alone (allowTeams stays true after
      // trial-expiry / hard Stripe cancel), else an expired-trial account with zero created
      // teams is wrongly blocked and locked out. Mirrors the non-inactive branch above.
      if (fromInactive && companyFeature?.allowTeams === true && hasCreatedTeams) {
        const planDisplayName = getPlanDisplayName('free');
        setChangePlanWarnings([
          {
            requiredRemove: true,
            url: undefined,
            messageDisplay: t('TEAMS_NOT_SUPPORTED_UPGRADE_REQUIRED', {
              plan: planDisplayName,
              defaultValue: `The ${planDisplayName} Plan does not support teams. Please upgrade to Pro Plus or higher to continue.`,
            }),
            plan: planDisplayName,
          },
        ]);
        setShowChangePlanWarning(true);
        return;
      }

      // Cancel-flow short-circuit: skip the "Why are you downgrading?" survey
      // since the user already answered the cancel-side reason picker. Jump
      // straight to the Standard+ vs Free comparison dialog. Two signals can
      // mark this entry path:
      //   - `fromCancelFlowRef.current` — set by `enterPlanStepFromCancelFlow`
      //     when the user picks "I want to switch plans" and lands on the
      //     wizard's PLAN step.
      //   - `cancelFlowSource === 'too_expensive'` — set by
      //     `CancelSubscriptionDialog.handleSwitchToStandard` when the user
      //     picks "It's too expensive" and accepts the "Switch to Free"
      //     retention offer (handleSelectFree fires without a wizard step
      //     change, so the ref is never set on this path).
      const fromCancelFlow = fromCancelFlowRef.current || cancelFlowSource !== null;
      if (fromCancelFlow) {
        openDowngradeComparison({ subAccountId: contextSubAccountId });
      } else {
        openDowngradeSurvey({ subAccountId: contextSubAccountId });
      }
    } finally {
      selectingFreeRef.current = false;
      setIsSelectingFree(false);
    }
  }, [
    fromInactive,
    companyFeature,
    fetchMembersForDowngrade,
    openMemberDowngradeWarning,
    openDowngradeSurvey,
    openDowngradeComparison,
    setChangePlanWarnings,
    setShowChangePlanWarning,
    contextSubAccountId,
    cancelFlowSource,
    isDefaultTeam,
    hasCreatedTeams,
    t,
  ]);

  /**
   * Called when the cancel-flow hands the user off to the PLAN step via the
   * "I want to switch plans" reason. Sets the bypass flag so picking Free here
   * skips the redundant DowngradeSurveyDialog.
   */
  const enterPlanStepFromCancelFlow = useCallback(() => {
    fromCancelFlowRef.current = true;
    setStep(STEP.PLAN);
  }, []);

  // ---- Change plan warning handlers (paid-tier downgrade on Confirmation step) ----
  const dismissChangePlanWarning = useCallback(() => {
    setShowChangePlanWarning(false);
    setChangePlanWarningConfirmLabel(undefined);
  }, []);

  const confirmChangePlanWarning = useCallback(() => {
    setShowChangePlanWarning(false);
    setChangePlanWarningConfirmLabel(undefined);
    // User acknowledged the warning — advance the wizard and skip re-validation.
    skipNextValidationRef.current = true;
    doGoNext();
  }, [doGoNext]);

  return {
    // Dialog state
    isOpen,
    handleOpenChange,

    // Step navigation
    step,
    setStep,
    stepIndex,
    goNext,
    goBack,
    canGoNext,
    validatingPlan,
    isSelectingFree,
    skippedToConfirmation,
    setSkippedToConfirmation,

    // Screen state
    screenCount,
    setScreenCount: handleScreenCountChange,
    minScreens,
    maxScreens,
    usedScreens,

    // Team state (populated only when opened with teamId context — Issue #49)
    teamNameInput,
    setTeamNameInput,
    teamDescriptionInput,
    setTeamDescriptionInput,

    // Plan state
    selectedPlan,
    submitPlanAlias,
    setSelectedPlan,
    // #1275 — team / no-Team-plan downgrade guard
    handleSelectPlan,
    teamPlanBlockOpen,
    dismissTeamPlanBlock,
    switchToDefaultTeam,
    currentPlanFeature,
    isOnFreePlan,
    // #1413 — cancelled/inactive account re-subscribing has no active Stripe
    // subscription to prorate, so the wizard's "Due Today" must mirror Subtotal
    // (same case as trial/free in #1411). Exposed so WizardStepConfirmation can
    // include it in `hasNoActiveSubscription`.
    isAccountInactive,
    planFeatureId,
    aliasData,
    aliasPlanData,
    planInfoText,
    planName,

    // Billing state
    billingInterval,
    setBillingInterval,
    /** Customer's CURRENT billing interval (from existing subscription).
     * Stays fixed while `billingInterval` mutates as the user edits the wizard.
     * Used by the paywall (#1134) to hide the Monthly/Yearly toggle on Upgrade
     * Required when the customer is already on Yearly — matches scio1 behavior. */
    currentBillingInterval: initialInterval,
    monthlyPrice,
    yearlyPrice,
    yearlyFreeDevicePrice,
    currentPricePerScreen,
    showYearlyFreeDevice,
    yearlyFreeDeviceDisabled,
    monthlyDisabled,
    yearlyDisabled,
    // Per-bundle free-device editing (SCIO 1.0 parity, yearly_freedevice only)
    editedDeviceFrees,
    initEditedDeviceFrees,
    setBundleQuantity,
    currency: effectiveCurrency,
    currencySymbol: effectiveCurrencySymbol,
    setCurrency: setCurrencyOverride,
    addonTotal,
    addonItems,

    // Payment/card state
    existingCard,
    useExistingCard,
    setUseExistingCard,
    isTrial,

    // Stripe
    stripeLoading,
    stripeClientSecret,
    stripePaymentMethodOrder,
    stripeReturnUrl,
    defaultEmail,

    // Review
    reviewResult,
    reviewLoading,
    initialCredit,

    // Promo
    promoCode,
    setPromoCode,
    discountLabel,
    showPromoCode,
    setShowPromoCode: () => setShowPromoCode(true),
    handleApplyPromo,
    handleRemovePromo,

    // Billing address (for AddressElement → redirect flow)
    handleBillingAddressChange,

    // Charge
    isSubmitting,
    stripeProcessing,
    setStripeProcessing,
    chargeResult,
    handleCharge,
    handleConfirmExistingCard,
    handlePaidUpdate,
    handleStripeSuccess,

    // Screen management
    showScreenManagement,
    setShowScreenManagement,

    // Free plan / downgrade
    handleSelectFree,

    // Cancel-flow handoff (CancelSubscriptionDialog → Switch to Standard)
    switchPlanFromCancelFlow,
    // Cancel-flow handoff (CancelSubscriptionDialog → "I want to switch plans")
    enterPlanStepFromCancelFlow,

    // Change plan warning
    showChangePlanWarning,
    changePlanWarnings,
    changePlanWarningConfirmLabel,
    dismissChangePlanWarning,
    confirmChangePlanWarning,

    // Sub-account / team billing context
    isSubAccount: !!contextSubAccountId,
    contextSubAccountId,
    contextTeamId,
    skipPlanStep,
    contextMinPlan,
    contextTitle,
    loadingSubAccountData: !subAccountDataReady,

    // Constants re-exported for convenience
    STEP,
  } as const;
}
